import { Button } from '@heroui/react';
import { TimerType } from '@/types';
import { PlacementBadge, ProductThumbnailPlaceholders } from '@components/preview/productPageMock';
import { isProductContainerCreateEnabled } from '@lib/timerTypeGates';

type TimerTypeSelectionStepProps = {
	onSelect: (timerType: TimerType) => void;
};

const TIMER_TYPE_OPTIONS = [
	{
		type: TimerType.BAR,
		title: 'Bar',
		description: 'Narrow horizontally aligned timer. Best for the top or bottom of any page.',
		isEnabled: true,
	},
	{
		type: TimerType.PRODUCT_CONTAINER,
		title: 'Product container',
		description: 'Small timer with no or solid background. Best for the product page.',
		isEnabled: isProductContainerCreateEnabled(),
	},
] as const;

const ILLUSTRATION_TIMER_VALUES = [
	{ id: 'days', value: '0' },
	{ id: 'hours', value: '11' },
	{ id: 'minutes', value: '59' },
	{ id: 'seconds', value: '59' },
] as const;

const BAR_PRODUCT_PLACEHOLDERS = ['bar-product-1', 'bar-product-2', 'bar-product-3', 'bar-product-4'] as const;

export default function TimerTypeSelectionStep({ onSelect }: TimerTypeSelectionStepProps) {
	const visibleTimerTypeOptions = TIMER_TYPE_OPTIONS.filter((option) => option.isEnabled);

	return (
		<div className='mx-auto grid w-full max-w-[1424px] grid-cols-1 gap-6 lg:grid-cols-2'>
			{visibleTimerTypeOptions.map((option) => (
				<article
					key={option.type}
					className='grid overflow-hidden rounded-[22px] border border-[#E0E4EA] bg-white shadow-sm lg:grid-rows-[300px_auto] xl:grid-rows-[320px_auto]'
				>
					{option.type === TimerType.BAR ? <BarTypeIllustration /> : <ProductContainerTypeIllustration />}
					<div className='grid min-h-[138px] grid-cols-1 gap-4 p-5 sm:grid-cols-[1fr_auto] sm:items-end lg:p-6'>
						<div className='min-w-0'>
							<h3 className='font-lato text-xl font-bold leading-7 text-[#111827]'>{option.title}</h3>
							<p className='mt-3 max-w-[480px] font-lato text-base leading-6 text-[#4B5563]'>{option.description}</p>
						</div>
						<Button
							variant='primary'
							className='h-11 min-w-24 rounded-lg px-7 text-base font-semibold'
							onPress={() => onSelect(option.type)}
						>
							Select
						</Button>
					</div>
				</article>
			))}
		</div>
	);
}

function BarTypeIllustration() {
	return (
		<div className='h-full min-h-[300px] overflow-hidden bg-[#303647] p-6 xl:min-h-[320px]'>
			<div className='relative rounded-xl border-2 border-dashed border-[#747D9A] p-3'>
				<div className='flex h-9 items-center rounded bg-[#3E63F4] px-4'>
					<div className='h-3 w-20 rounded bg-white/35 sm:w-28' />
					<div className='mx-auto flex items-center gap-1.5 font-lato text-base font-bold text-white'>
						{ILLUSTRATION_TIMER_VALUES.map((item) => (
							<span key={item.id} className='rounded bg-[#29314E] px-1.5 py-0.5'>
								{item.value}
							</span>
						))}
					</div>
					<div className='h-4 w-20 rounded-full bg-white/35 sm:w-28' />
				</div>
				<PlacementBadge />
			</div>
			<div className='mt-6 h-4 w-52 max-w-[34%] rounded bg-[#46506A]' />
			<div className='mt-6 grid grid-cols-4 gap-4'>
				{BAR_PRODUCT_PLACEHOLDERS.map((id) => (
					<div key={id} className='h-[64px] rounded-lg bg-[#47516A] xl:h-[72px]' />
				))}
			</div>
			<div className='mt-4 h-[64px] rounded-xl bg-[#47516A] xl:h-[78px]' />
		</div>
	);
}

function ProductContainerTypeIllustration() {
	return (
		<div className='grid h-full min-h-[300px] grid-cols-1 gap-4 overflow-hidden bg-[#303647] p-6 sm:grid-cols-[1.05fr_0.95fr] sm:gap-6 xl:min-h-[320px]'>
			<div className='flex min-w-0 flex-col gap-3 sm:gap-4'>
				<div className='h-[92px] rounded-xl bg-[#46506A] sm:h-[166px] xl:h-[194px]' />
				<ProductThumbnailPlaceholders className='h-9 sm:h-[56px] xl:h-[64px]' />
			</div>
			<div className='flex min-w-0 flex-col gap-3 pt-1'>
				<div className='h-8 w-36 max-w-full rounded bg-[#46506A] xl:w-44' />
				<div className='h-4 rounded bg-[#46506A]' />
				<div className='h-4 rounded bg-[#46506A]' />
				<div className='relative rounded-xl border-2 border-dashed border-[#747D9A] p-2 sm:p-3'>
					<div className='rounded bg-[#3E63F4] px-4 py-3 sm:px-5 sm:py-4'>
						<div className='h-3 w-20 rounded bg-white/35' />
						<div className='mt-3 h-3 w-32 rounded bg-white/35 xl:w-40' />
						<div className='mt-4 flex items-center justify-center gap-1.5 font-lato text-sm font-bold text-white sm:mt-5 sm:gap-2 sm:text-base'>
							{ILLUSTRATION_TIMER_VALUES.map((item) => (
								<span key={item.id} className='rounded bg-[#29314E] px-1.5 py-0.5'>
									{item.value}
								</span>
							))}
						</div>
					</div>
					<PlacementBadge />
				</div>
				<div className='mt-auto grid grid-cols-2 gap-4'>
					<div className='h-11 rounded bg-[#46506A] xl:h-12' />
					<div className='h-11 rounded bg-[#46506A] xl:h-12' />
				</div>
				<div className='h-3 rounded bg-[#46506A]' />
			</div>
		</div>
	);
}
